<template>
  <div class="questionnaireList">
    <ds-header title="问卷列表"></ds-header>

    <!-- 搜索筛选表单 -->
    <a-form layout="inline" style="margin-bottom: 10px">
      <a-form-item label="问卷标题：">
        <a-input v-model.trim="searchForm.title" placeholder="请输入问卷标题搜索" style="width: 200px" />
      </a-form-item>
      <a-form-item label="状态">
        <a-select v-model="searchForm.status" placeholder="请选择状态" style="width: 200px"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode">
          <a-select-option value="">全部</a-select-option>
          <a-select-option :value="QuestionnaireStatusEnum.ONGOING">进行中</a-select-option>
          <a-select-option :value="QuestionnaireStatusEnum.ENDED">已结束</a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="生效时间">
        <a-range-picker v-model="timeRange" @change="handleTimeRangeChange" :placeholder="['开始时间', '结束时间']"
          format="YYYY-MM-DD HH:mm" :showTime="{ format: 'HH:mm' }"
          :getCalendarContainer="(triggerNode) => triggerNode.parentNode" style="width: 380px" />
      </a-form-item>
      <span>
        <a-button type="primary" @click="search" icon="search">查询</a-button>
        <a-button style="margin-left: 8px" @click="resetSearchForm" icon="redo">重置</a-button>
      </span>
    </a-form>

    <!-- 新建按钮 -->
    <div style="margin: 10px 0">
      <a-button type="primary" @click="addQuestionnaire">
        <a-icon type="plus" />新建问卷
      </a-button>
    </div>

    <!-- 数据表格 -->
    <a-spin :spinning="loading" tip="加载中...">
      <a-table :rowKey="(record) => record.id" :columns="table.columns" :data-source="table.tableData"
        :pagination="table.pagination.total ? {
          current: table.pagination.page,
          pageSize: table.pagination.size,
          total: table.pagination.total,
          pageSizeOptions: table.pagination.pageSizeOptions,
          showSizeChanger: table.pagination.showSizeChanger,
          showQuickJumper: table.pagination.showQuickJumper,
          showTotal: table.pagination.showTotal,
        } : false" @change="pageChange" bordered :scroll="{ x: 1400 }">
        <!-- 问卷标题列 -->
        <div slot="questionnaireTitle" slot-scope="text, record">
          <a-tooltip>
            <template slot="title">
              <span>{{ record.title }}</span>
            </template>
            <div class="ellipsis-line" @click="toDetail(record)" style="cursor: pointer; color: #1890ff;">
              {{ record.title }}
            </div>
          </a-tooltip>
        </div>

        <!-- 状态列 -->
        <span slot="status" slot-scope="text">
          <a-tag v-if="getStatusConfig(text).style" :style="getStatusConfig(text).style">
            {{ getStatusConfig(text).text }}
          </a-tag>
          <a-tag v-else :color="getStatusConfig(text).color">
            {{ getStatusConfig(text).text }}
          </a-tag>
        </span>

        <!-- 操作列 -->
        <span slot="action" slot-scope="text, record" class="action">
          <a-button type="link" @click="toDetail(record)">查看</a-button>
          <a-button type="link" @click="edit(record)" v-if="canEdit(record)">编辑</a-button>
          <a-button type="link" @click="deleteQuestionnaire(record)" v-if="canDelete(record)">删除</a-button>
          <a-button type="link" @click="endQuestionnaire(record)" v-if="canEnd(record)">{{ getEndButtonText(record) }}</a-button>
          <a-button type="link" @click="generateLink(record)">生成链接</a-button>
          <a-button type="link" @click="exportQuestionnaire(record)">导出</a-button>
        </span>
      </a-table>
    </a-spin>

    <!-- 删除确认弹窗 -->
    <a-modal title="删除确认" :visible="deleteVisible" :confirm-loading="confirmLoading" @ok="handleDeleteOk"
      @cancel="deleteVisible = false">
      <p>确定要删除问卷「{{ currentRow.title }}」吗？</p>
      <p style="color: #ff4d4f;">删除后数据将无法恢复，请谨慎操作！</p>
    </a-modal>

    <!-- 结束确认弹窗 -->
    <a-modal title="结束问卷" :visible="endVisible" :confirm-loading="confirmLoading" @ok="handleEndOk"
      @cancel="endVisible = false">
      <p>确定要结束问卷「{{ currentRow.title }}」吗？</p>
      <p style="color: #ff4d4f;">结束后用户将无法填写该问卷，且无法恢复为进行中状态！</p>
    </a-modal>

    <!-- 生成链接弹窗 -->
    <a-modal title="H5问卷链接" :visible="linkVisible" :footer="null" @cancel="linkVisible = false">
      <div>
        <p>问卷链接如下：</p>
        <a-input :value="questionnaireLink" readonly id="questionnaireLinkInput">
          <a-icon slot="suffix" type="copy" @click="copyLink" style="cursor: pointer" />
        </a-input>
      </div>
      <div style="margin-top: 16px; text-align: right;">
        <a-button type="primary" @click="copyLink">复制链接</a-button>
        <a-button style="margin-left: 8px" @click="linkVisible = false">关闭</a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment';
import * as api from '@/api/questionnaireManage';
import { dealExcelRespose } from '@/utils/download';
import { QuestionnaireStatusEnum, StatusConfig, TableColumns } from './help';

export default {
  name: 'QuestionnaireList',
  data() {
    return {
      QuestionnaireStatusEnum,  // 枚举引用，使模板可访问
      // 加载状态
      loading: false,
      // 搜索表单
      searchForm: {
        title: '',
        status: '',
        startTimeBegin: '',
        startTimeEnd: '',
      },
      // 生效时间范围
      timeRange: [],
      // 表格配置
      table: {
        columns: TableColumns,
        tableData: [],
        pagination: {
          pageSizeOptions: ['10', '20', '30', '50', '100'],
          showSizeChanger: true,
          page: 1,
          size: 10,
          total: 0,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`
        }
      },
      // 弹窗状态
      deleteVisible: false,
      endVisible: false,
      linkVisible: false,
      confirmLoading: false,
      // 当前操作行
      currentRow: {},
      // 问卷链接
      questionnaireLink: ''
    };
  },

  mounted() {
    this.getList();
  },

  methods: {
    // ========== 数据获取 ==========

    /**
     * 获取问卷列表
     * 说明：接口暂未开发，当前使用 Mock 数据
     * 接口开发完成后，将下方注释的 API 调用代码恢复，删除 Mock 数据代码
     */
    getList() {
      const data = {
        page: this.table.pagination.page,
        size: this.table.pagination.size,
        ...this.searchForm,
      };

      api.getQuestionnaireList(data).then((res) => {
        if (res.code === '200') {
          this.table.tableData = res.data.items || [];
          this.table.pagination.total = res.data.total || 0;
        } else {
          this.$message.error(res.message || '获取列表失败');
        }
      }).catch((error) => {
        console.error('获取问卷列表失败:', error);
        this.$message.error('获取列表失败，请稍后重试');
      });
    },

    // ========== 搜索操作 ==========

    /**
     * 搜索
     */
    search() {
      this.table.pagination.current = 1;
      this.getList();
    },

    /**
     * 重置搜索表单
     */
    resetSearchForm() {
      this.searchForm = {
        title: '',
        status: '',
        startTimeBegin: '',
        startTimeEnd: '',
      };
      this.timeRange = [];
      this.table.pagination.page = 1;
      this.getList();
    },

    /**
     * 时间选择器 change 处理
     */
    handleTimeRangeChange(dates) {
      if (dates && dates.length === 2) {
        this.searchForm.startTimeBegin = dates[0].format('YYYY-MM-DD HH:mm');
        this.searchForm.startTimeEnd = dates[1].format('YYYY-MM-DD HH:mm');
      } else {
        this.searchForm.startTimeBegin = '';
        this.searchForm.startTimeEnd = '';
      }
    },

    /**
     * 分页变化
     */
    pageChange(option) {
      this.table.pagination.page = option.current;
      this.table.pagination.size = option.pageSize;
      this.getList();
    },

    // ========== 操作权限判断 ==========

    /**
     * 是否可编辑（无提交数据时可编辑）
     */
    canEdit(record) {
      // 优先使用后端字段
      if (record.showEditButton !== undefined) {
        return record.showEditButton;
      }
      // 降级逻辑：无提交数据时可编辑
      return record.submitCount === 0;
    },

    /**
     * 是否可删除（无提交记录时可删除）
     */
    canDelete(record) {
      // 优先使用后端字段
      if (record.showDeleteButton !== undefined) {
        return record.showDeleteButton;
      }
      // 降级逻辑：无提交记录时可删除
      return record.submitCount === 0;
    },

    /**
     * 是否可结束（进行中状态可结束）
     */
    canEnd(record) {
      // 优先使用后端字段
      if (record.showEndButton !== undefined) {
        return record.showEndButton;
      }
      // 降级逻辑：只要不是结束状态就可以结束
      return record.status !== QuestionnaireStatusEnum.ENDED;
    },

    /**
     * 获取状态配置
     */
    getStatusConfig(status) {
      return StatusConfig[status] || { text: '未知', color: 'default' };
    },

    /**
     * 获取结束按钮文案
     * 未开始状态显示"关闭"，其他状态显示"结束"
     */
    getEndButtonText(record) {
      return record.status === QuestionnaireStatusEnum.NOT_STARTED ? '关闭' : '结束';
    },

    // ========== 路由跳转 ==========

    /**
     * 新建问卷
     */
    addQuestionnaire() {
      this.$router.push({
        path: '/questionnaireManage/questionnaireList/questionnaireAddForm',
      })
    },

    /**
     * 查看详情
     */
    toDetail(record) {
      this.$router.push({
        path: '/questionnaireManage/questionnaireList/questionnaireDetail',
        query: {
          questionnaireNo: record.id,
        },
      })
    },

    /**
     * 编辑问卷
     */
    edit(record) {
      this.$router.push({
        path: '/questionnaireManage/questionnaireList/questionnaireEditForm',
        query: {
          questionnaireNo: record.id,
        },
      })
    },

    // ========== 删除操作 ==========

    /**
     * 删除问卷（弹出确认框）
     */
    deleteQuestionnaire(record) {
      this.currentRow = record;
      this.deleteVisible = true;
    },

    /**
     * 确认删除
     */
    handleDeleteOk() {
      this.confirmLoading = true;

      api.deleteQuestionnaire(this.currentRow.id).then((res) => {
        if (res.code === '200') {
          this.$message.success('删除成功');
          this.deleteVisible = false;
          this.getList();
        } else {
          this.$message.error(res.message || '删除失败');
        }
      }).catch((error) => {
        console.error('删除问卷失败:', error);
        this.$message.error('删除失败，请稍后重试');
      }).finally(() => {
        this.confirmLoading = false;
      });
    },

    // ========== 结束操作 ==========

    /**
     * 结束问卷（弹出确认框）
     */
    endQuestionnaire(record) {
      this.currentRow = record;
      this.endVisible = true;
    },

    /**
     * 确认结束
     */
    handleEndOk() {
      this.confirmLoading = true;

      api.endQuestionnaire(this.currentRow.id).then((res) => {
        if (res.code === '200') {
          this.$message.success('问卷已结束');
          this.endVisible = false;
          this.getList();
        } else {
          this.$message.error(res.message || '操作失败');
        }
      }).catch((error) => {
        console.error('结束问卷失败:', error);
        this.$message.error('操作失败，请稍后重试');
      }).finally(() => {
        this.confirmLoading = false;
      });
    },

    // ========== 生成链接 ==========

    /**
     * 生成H5链接
     */
    generateLink(record) {
      const baseUrl = process.env.VUE_APP_BASE_API;
      this.questionnaireLink = `${baseUrl}/apps/appcv/questionnaire?questionnaireNo=${record.questionnaireNo}`;
      this.currentRow = record;
      this.linkVisible = true;
    },

    /**
     * 复制链接
     */
    copyLink() {
      const input = document.getElementById('questionnaireLinkInput');
      input.select();
      input.setSelectionRange(0, 99999); // 兼容移动端

      try {
        document.execCommand('copy');
        this.$message.success('链接已复制到剪贴板');
      } catch (error) {
        // 降级方案：使用 navigator.clipboard
        if (navigator.clipboard) {
          navigator.clipboard.writeText(this.questionnaireLink).then(() => {
            this.$message.success('链接已复制到剪贴板');
          }).catch(() => {
            this.$message.error('复制失败，请手动复制');
          });
        } else {
          this.$message.error('复制失败，请手动复制');
        }
      }
    },

    /**
     * 导出问卷数据
     */
    async exportQuestionnaire(record) {
      const loading = this.$message.loading('导出中...', 0);

      try {
        const res = await api.exportQuestionnaireData(record.id);

        // 处理Blob响应
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        // 生成文件名（清理特殊字符）
        const filename = `${record.title}_问卷数据.xlsx`.replace(/[\\/:*?"<>|]/g, '_');

        // 使用工具函数下载
        dealExcelRespose(blob, filename);

        this.$message.success('导出成功');
      } catch (error) {
        console.error('导出问卷失败:', error);

        // 判断是否是业务错误
        if (error.response && error.response.data) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result);
              this.$message.error('导出失败：' + (errorData.message || '未知错误'));
            } catch {
              this.$message.error('导出失败，请稍后重试');
            }
          };
          reader.readAsText(error.response.data);
        } else {
          this.$message.error('导出失败：' + (error.message || '网络错误'));
        }
      } finally {
        loading();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.questionnaireList {
  padding: 16px;

  .ellipsis-line {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 280px;
  }

  .action {
    .ant-btn-link {
      padding: 0;
      margin-right: 5px;

      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>
